// Picking a value anchors that field; the other two resolve from every reference row (a valid
// LB->AB->SA combo) linked to it. Selection is a set of those rows, never a cross product, so
// trimming one field cascades to the others on its own.

export type FieldKey = "lb" | "ab" | "sa";

// A reference row can have no Submission Authority. The absence still travels with the link,
// so it carries this sentinel: it renders as "None" and is never offered as an anchor, since
// anchoring on the lack of a value selects nothing meaningful.
export const NO_LINK_VALUE = "-";

// lb/ab/sa are the display labels the anchor logic matches on; the ids are what the save
// needs. An Accreditation Body is a Qualification, so accreditationBodyId is the
// SkuQualification QualificationId.
export interface ReferenceRow {
  lb: string;
  ab: string;
  sa: string;
  licensureBodyId: string;
  accreditationBodyId: string;
}

export interface LinkState {
  anchorField: FieldKey | null;
  anchorValue: string | null;
  links: ReferenceRow[]; // resolved reference rows (one entry each)
}

export const emptyLinkState = (): LinkState => ({
  anchorField: null,
  anchorValue: null,
  links: [],
});

export const hasAnchor = (
  s: LinkState,
): s is LinkState & { anchorField: FieldKey; anchorValue: string } =>
  s.anchorField != null && s.anchorValue != null;

const distinct = (values: string[]): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const v of values) {
    if (!seen.has(v)) {
      seen.add(v);
      out.push(v);
    }
  }
  return out;
};

// Distinct values of a field across ALL reference rows — the free-search options offered
// before an anchor is chosen.
export function allValues(rows: ReferenceRow[], field: FieldKey): string[] {
  return distinct(rows.map((r) => r[field])).filter((v) => v !== NO_LINK_VALUE);
}

// Pick a value in a field -> that field becomes the anchor; links resolve to every row
// carrying it.
export function selectAnchor(
  rows: ReferenceRow[],
  field: FieldKey,
  value: string,
): LinkState {
  return {
    anchorField: field,
    anchorValue: value,
    links: rows.filter((r) => r[field] === value),
  };
}

// Distinct values shown as chips for a field. For the anchor field this is just
// [anchorValue]; for the others it's every value the resolved links carry.
export function chipsFor(s: LinkState, field: FieldKey): string[] {
  return distinct(s.links.map((l) => l[field]));
}

const linkKey = (r: ReferenceRow): string => `${r.lb}|${r.ab}|${r.sa}`;

// The reference rows the anchor resolves to. Every add-back is limited to these, so a
// combination absent from the reference table can never be built.
const anchorRows = (rows: ReferenceRow[], s: LinkState): ReferenceRow[] =>
  hasAnchor(s) ? rows.filter((r) => r[s.anchorField] === s.anchorValue) : [];

// Drops every link carrying the value. Chips read off the links, so a value in the other
// non-anchor field that loses all of its links disappears with it.
export function removeValue(
  s: LinkState,
  field: FieldKey,
  value: string,
): LinkState {
  return { ...s, links: s.links.filter((l) => l[field] !== value) };
}

// Locked when every remaining link carries it: removing it would empty the set, so the only
// way to clear it is clearing the whole selection via the anchor.
export function isLocked(
  s: LinkState,
  field: FieldKey,
  value: string,
): boolean {
  return s.links.length > 0 && s.links.every((l) => l[field] === value);
}

// Values the anchor allows for this field that are not currently selected.
export function addOptions(
  rows: ReferenceRow[],
  s: LinkState,
  field: FieldKey,
): string[] {
  const selected = new Set(chipsFor(s, field));
  return distinct(anchorRows(rows, s).map((r) => r[field])).filter(
    (v) => !selected.has(v),
  );
}

// Restores the links the anchor permits for that value, and nothing else.
export function addValue(
  rows: ReferenceRow[],
  s: LinkState,
  field: FieldKey,
  value: string,
): LinkState {
  const known = new Set(s.links.map(linkKey));
  const restored = anchorRows(rows, s).filter(
    (r) => r[field] === value && !known.has(linkKey(r)),
  );
  return { ...s, links: [...s.links, ...restored] };
}
